import React, { useMemo, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import {
    Check,
    Star,
    ShieldCheck,
    Sparkles,
    ArrowRight,
    Zap,
    CheckCircle2,
} from 'lucide-react';

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: 'easeOut',
        },
    },
};

const PLANS = {
    free: {
        name: 'Free',
        badge: 'Get started',
        description: 'Perfect for building the habit and tracking your essentials.',
        monthly: 0,
        yearly: 0,
        cta: 'Start Free',
        highlight: 'Track your day with zero cost',
        features: [
            'Step tracking + daily goals',
            'Meal, water, workout and weight logging',
            'Basic progress trends',
            'Daily summary and streaks',
            'Health Connect sync',
        ],
        notIncluded: [
            'AI Meal Deep Dives',
            'Weekly Intelligence Reports',
            '24/7 AI Coach',
            'Advanced trend analysis',
            'Daily AI missions',
        ],
    },
    pro: {
        name: 'Pro',
        badge: 'Most popular',
        description: 'For users who want daily AI guidance, smarter feedback, and deeper insights.',
        monthly: 10,
        yearly: 96,
        cta: 'Upgrade to Pro',
        highlight: 'Your full AI-powered weight-loss companion',
        features: [
            'Everything in Free',
            'Unlimited AI Meal Deep Dives',
            'Weekly Intelligence Reports',
            '24/7 Interactive AI Coach',
            'Hydration and workout insights',
            'Advanced plateau detection',
            'Daily behavioral missions',
            'Deeper progress and pattern analysis',
        ],
        notIncluded: [],
    },
} as const;

export default function Pricing() {
    const [isYearly, setIsYearly] = useState(false);

    const proPrice = useMemo(() => {
        return isYearly ? PLANS.pro.yearly : PLANS.pro.monthly;
    }, [isYearly]);

    const billingLabel = isYearly ? '/year' : '/month';
    const effectiveMonthly = Math.round(PLANS.pro.yearly / 12);

    const surfaceClass =
        'bg-[linear-gradient(180deg,var(--color-brand-card),var(--color-brand-ai-bg))] border border-[var(--color-brand-border)] shadow-[0_18px_50px_rgba(15,23,42,0.08)]';

    return (
        <section id="pricing" className="relative overflow-hidden py-24 md:py-32">
            {/* Background */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.10),transparent_35%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.08),transparent_28%)]" />
                <div className="absolute left-[-6%] top-20 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="absolute right-[-8%] bottom-8 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.45 }}
                    className="mx-auto mb-16 max-w-4xl text-center"
                >
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)] shadow-[0_8px_30px_rgba(34,211,238,0.10)]">
                        <Sparkles size={14} />
                        Simple pricing
                    </div>

                    <h2 className="text-4xl font-black leading-[1.05] text-[var(--color-brand-text)] md:text-6xl">
                        Start free and upgrade when you want
                        <span className="block bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-transparent">
              smarter AI coaching
            </span>
                    </h2>

                    <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--color-brand-text-secondary)] md:text-xl">
                        LesslyKilo gives you the essentials for free, then unlocks deeper insights,
                        coaching, and behavior support with Pro.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        {['No hidden fees', 'Upgrade anytime', 'Built for sustainable results'].map((item) => (
                            <div
                                key={item}
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)] shadow-[0_8px_24px_rgba(15,23,42,0.06)]"
                            >
                                <CheckCircle2 size={16} className="text-emerald-400" />
                                {item}
                            </div>
                        ))}
                    </div>

                    {/* Billing toggle */}
                    <div className="mt-10 flex items-center justify-center gap-4">
            <span
                className={`text-sm font-bold transition-colors ${
                    !isYearly ? 'text-[var(--color-brand-text)]' : 'text-[var(--color-brand-text-secondary)]'
                }`}
            >
              Monthly
            </span>

                        <button
                            onClick={() => setIsYearly((prev) => !prev)}
                            className="relative h-10 w-16 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] p-1 shadow-[0_8px_20px_rgba(34,211,238,0.10)] transition-all"
                            aria-label="Toggle billing period"
                        >
                            <motion.div
                                animate={{ x: isYearly ? 24 : 0 }}
                                transition={{ duration: 0.22 }}
                                className="h-8 w-8 rounded-full bg-[linear-gradient(135deg,var(--color-brand-primary),#34d399)] shadow-[0_6px_16px_rgba(34,211,238,0.28)]"
                            />
                        </button>

                        <span
                            className={`inline-flex items-center gap-2 text-sm font-bold transition-colors ${
                                isYearly ? 'text-[var(--color-brand-text)]' : 'text-[var(--color-brand-text-secondary)]'
                            }`}
                        >
              Yearly
              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-black uppercase tracking-wide text-emerald-500">
                Save 20%
              </span>
            </span>
                    </div>
                </motion.div>

                {/* Pricing cards */}
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ staggerChildren: 0.08 }}
                    className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-2"
                >
                    {/* Free */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -6 }}
                        className={`relative flex h-full flex-col overflow-hidden rounded-[2rem] p-8 md:p-10 ${surfaceClass}`}
                    >
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-cyan-400/5 blur-2xl" />

                        <div className="relative z-10 mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-cyan-500">
                            <Zap size={13} />
                            {PLANS.free.badge}
                        </div>

                        <div className="relative z-10 mb-8">
                            <h3 className="text-2xl font-black text-[var(--color-brand-text)]">{PLANS.free.name}</h3>
                            <p className="mt-2 text-sm leading-6 text-[var(--color-brand-text-secondary)]">
                                {PLANS.free.description}
                            </p>

                            <div className="mt-6 flex items-end gap-2">
                <span className="bg-[linear-gradient(90deg,var(--color-brand-text),var(--color-brand-primary))] bg-clip-text text-5xl font-black tracking-tight text-transparent">
                  $0
                </span>
                                <span className="pb-1 text-sm font-bold text-[var(--color-brand-text-secondary)]">/forever</span>
                            </div>

                            <div className="mt-4 rounded-2xl border border-cyan-400/15 bg-cyan-400/8 p-4">
                                <p className="text-sm font-semibold text-[var(--color-brand-text)]">{PLANS.free.highlight}</p>
                            </div>
                        </div>

                        <div className="relative z-10 flex-1">
                            <div className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                                What’s included
                            </div>

                            <ul className="space-y-3.5">
                                {PLANS.free.features.map((feature) => (
                                    <li key={feature} className="flex items-start gap-3 text-[var(--color-brand-text)]">
                                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-cyan-400/20 bg-cyan-400/12 text-cyan-500">
                                            <Check size={12} />
                                        </div>
                                        <span className="text-sm font-medium leading-6">{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-8 mb-4 text-xs font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                                Upgrade for
                            </div>

                            <ul className="space-y-3">
                                {PLANS.free.notIncluded.map((feature) => (
                                    <li key={feature} className="flex items-start gap-3 text-[var(--color-brand-text-secondary)]">
                                        <div className="mt-[11px] h-px w-4 shrink-0 bg-[var(--color-brand-text-secondary)]/35" />
                                        <span className="text-sm leading-6">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/*<div className="relative z-10 mt-10">
                            <button disabled={true} className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-cyan-400/20 bg-[var(--color-brand-card)] py-4 text-sm font-black text-[var(--color-brand-text)] transition-all hover:border-cyan-400/30 hover:bg-cyan-400/6">
                                {PLANS.free.cta}
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                            </button>

                            <p className="mt-4 text-center text-[11px] text-[var(--color-brand-text-secondary)]">
                                Start tracking immediately
                            </p>
                        </div>*/}

                        <div className="relative z-10 mt-10">
                            <button
                                disabled
                                className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] py-4 text-sm font-black text-[var(--color-brand-text-secondary)] opacity-70 pointer-events-none"
                            >
                                {PLANS.free.cta}
                                <span className="rounded-full bg-[var(--color-brand-ai-bg)] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em]">
            Coming soon
        </span>
                            </button>

                            <p className="mt-4 text-center text-[11px] text-[var(--color-brand-text-secondary)]">
                                Start tracking immediately
                            </p>
                        </div>
                    </motion.div>

                    {/* Pro */}
                    <motion.div
                        variants={cardVariants}
                        whileHover={{ y: -6 }}
                        className={`relative flex h-full flex-col overflow-hidden rounded-[2rem] p-8 md:p-10 ${surfaceClass} border-[var(--color-brand-primary)]/20`}
                    >
                        <div className="pointer-events-none absolute -left-10 top-0 h-40 w-40 rounded-full bg-[var(--color-brand-primary)]/12 blur-3xl" />
                        <div className="pointer-events-none absolute bottom-0 right-0 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl" />
                        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(34,211,238,0.04),transparent_35%,rgba(16,185,129,0.05))]" />

                        <div className="relative z-10 mb-6 flex items-center justify-between gap-4">
                            <div className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-white shadow-[0_8px_24px_rgba(34,211,238,0.20)]">
                                <Star size={13} fill="currentColor" />
                                {PLANS.pro.badge}
                            </div>

                            {isYearly && (
                                <div className="rounded-full border border-[var(--color-brand-primary)]/20 bg-[var(--color-brand-primary)]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--color-brand-primary)]">
                                    ${effectiveMonthly}/mo billed yearly
                                </div>
                            )}
                        </div>

                        <div className="relative z-10 mb-8">
                            <h3 className="text-2xl font-black text-[var(--color-brand-text)]">{PLANS.pro.name}</h3>
                            <p className="mt-2 text-sm leading-6 text-[var(--color-brand-text-secondary)]">
                                {PLANS.pro.description}
                            </p>

                            <div className="mt-6 flex items-end gap-2">
                <span className="bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-5xl font-black tracking-tight text-transparent">
                  ${proPrice}
                </span>
                                <span className="pb-1 text-sm font-bold text-[var(--color-brand-text-secondary)]">{billingLabel}</span>
                            </div>

                            <div className="mt-4 rounded-2xl border border-[var(--color-brand-primary)]/18 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] p-4">
                                <p className="text-sm font-semibold text-[var(--color-brand-text)]">{PLANS.pro.highlight}</p>
                            </div>
                        </div>

                        <div className="relative z-10 flex-1">
                            <div className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-[var(--color-brand-primary)]">
                                What’s included
                            </div>

                            <ul className="space-y-3.5">
                                {PLANS.pro.features.map((feature) => (
                                    <li key={feature} className="flex items-start gap-3 text-[var(--color-brand-text)]">
                                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,var(--color-brand-primary),#34d399)] text-white shadow-[0_6px_16px_rgba(34,211,238,0.16)]">
                                            <Check size={12} />
                                        </div>
                                        <span className="text-sm font-medium leading-6">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/*<div className="relative z-10 mt-10">
                            <button disabled className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] py-4 text-sm font-black text-white shadow-[0_14px_30px_rgba(34,211,238,0.22)] transition-all hover:scale-[1.01] hover:brightness-110">
                                {PLANS.pro.cta}
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                            </button>

                            <p className="mt-4 text-center text-[11px] text-[var(--color-brand-text-secondary)]">
                                {isYearly ? 'Billed annually for the best value' : 'Switch anytime as your needs change'}
                            </p>
                        </div>*/}

                        <div className="relative z-10 mt-10">
                            <button
                                disabled
                                className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-card)] py-4 text-sm font-black text-[var(--color-brand-text-secondary)] opacity-70 pointer-events-none"
                            >
                                {PLANS.pro.cta}
                                <span className="rounded-full bg-[var(--color-brand-ai-bg)] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em]">
            Coming soon
        </span>
                            </button>

                            <p className="mt-4 text-center text-[11px] text-[var(--color-brand-text-secondary)]">
                                {isYearly ? 'Billed annually for the best value' : 'Switch anytime as your needs change'}
                            </p>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Bottom strip */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.08 }}
                    className="mt-12 rounded-[2rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(16,185,129,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)] md:p-8"
                >
                    <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
                        <div>
                            <h3 className="text-xl font-black text-[var(--color-brand-text)] md:text-2xl">
                                Free to start. Powerful when you are ready for more.
                            </h3>
                            <p className="mt-2 max-w-3xl text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-base">
                                Use Free for everyday tracking, then unlock Pro when you want AI coaching,
                                smarter feedback, and deeper support for consistency and progress.
                            </p>
                        </div>

                        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]">
                            <ShieldCheck size={16} className="text-emerald-500" />
                            Secure checkout via Play Store
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}